// Hook especializado para tracking del proceso de puja
// Separado de usePropertyBidding para mantener Single Responsibility Principle

import { useRef, useEffect } from 'react';

/**
 * Hook para tracking completo del proceso de puja
 * Maneja contadores, timers y eventos específicos de bidding
 * 
 * @param {function} trackEvent - Función de tracking de useEngagementTracking
 * @param {string} bidAmount - Cantidad actual de la oferta
 * @param {array} selectedConditions - Condiciones seleccionadas
 * @param {string} conditionsText - Texto de condiciones custom
 * @returns {object} Funciones de tracking específicas para pujas
 */
export const useBiddingTracking = (
  trackEvent,
  bidAmount,
  selectedConditions,
  conditionsText
) => {
  // Refs para tracking
  const formFocusTimeRef = useRef(null);
  const amountChangesRef = useRef(0);
  const conditionChangesRef = useRef(0);
  const submitAttemptsRef = useRef(0);
  const confirmAttemptsRef = useRef(0);
  const previousAmountRef = useRef('');
  const previousConditionsRef = useRef([]);

  /**
   * Inicializar timer del formulario (primera vez que escribe)
   */
  const initFormTimer = () => {
    if (!formFocusTimeRef.current) {
      formFocusTimeRef.current = Date.now();
    }
  };

  /**
   * Resetear todos los contadores (después de completar oferta)
   */
  const resetTracking = () => {
    formFocusTimeRef.current = null;
    amountChangesRef.current = 0;
    conditionChangesRef.current = 0;
    submitAttemptsRef.current = 0;
    confirmAttemptsRef.current = 0;
    previousAmountRef.current = '';
    previousConditionsRef.current = [];
  };

  /**
   * Calcular tiempo desde focus inicial
   */
  const getTimeInForm = () => {
    return formFocusTimeRef.current 
      ? Math.floor((Date.now() - formFocusTimeRef.current) / 1000)
      : 0;
  };

  /**
   * Helper: Determinar tipo de error de validación desde el objeto validation
   * Usa errorKey si está disponible (nuevo formato), sino hace fallback genérico
   */
  const determineErrorType = (validation) => {
    if (validation?.errorKey) {
      const key = validation.errorKey;
      if (key.includes('tooLow') || key.includes('belowMin') || key.includes('belowBlindOffer') || key.includes('belowMinIncrement')) return 'amount_too_low';
      if (key.includes('tooHigh') || key.includes('aboveMax')) return 'amount_too_high';
      if (key.includes('lastChanceUsed')) return 'already_used_last_chance';
      if (key.includes('invalidAmount')) return 'invalid_amount';
      if (key.includes('amountMismatch')) return 'amount_mismatch';
    }
    return 'validation_error';
  };

  // ============================================
  // TRACKING OPTIMIZADO: Solo cuando cantidad >= 5 dígitos (10,000+)
  // Reduce drásticamente el número de eventos generados
  // ============================================
  useEffect(() => {
    if (bidAmount && bidAmount !== previousAmountRef.current) {
      amountChangesRef.current += 1;

      const numericAmount = parseFloat(bidAmount) || 0;
      const previousNumeric = parseFloat(previousAmountRef.current) || 0;

      // Solo trackear si la nueva cantidad tiene 5+ dígitos (>= 10,000)
      // Y además es diferente al valor anterior trackeado
      if (trackEvent && numericAmount >= 10000) {
        // Evitar duplicados: solo si el valor numérico cambió significativamente
        if (Math.floor(numericAmount / 1000) !== Math.floor(previousNumeric / 1000)) {
          trackEvent('bid_amount_changed', {
            amount: numericAmount,
            changeCount: amountChangesRef.current,
            timeSinceFirstInput: getTimeInForm()
          });
        }
      }

      previousAmountRef.current = bidAmount;
    }
  }, [bidAmount, trackEvent]);

  // ============================================
  // FUNCIONES PÚBLICAS DE TRACKING
  // ============================================

  /**
   * Tracking: Cambio en condiciones (solo contador, no genera evento)
   * El resumen se incluye en bid_submit_success o bid_verification_email_sent
   */
  const trackConditionsChange = (newConditions) => {
    conditionChangesRef.current += 1;
    previousConditionsRef.current = newConditions;
    // No generamos evento individual - se consolida en el submit
  };

  /**
   * Tracking: Texto de condiciones custom (CONSOLIDADO)
   * Ya no genera evento - el dato se incluye en bid_verification_email_sent
   */
  const trackCustomConditionsWritten = () => {
    // No genera evento - consolidado en submit
  };

  /**
   * Tracking: Intento de envío fallido
   */
  const trackSubmitFailed = (validation, amount, currentPrice, property) => {
    submitAttemptsRef.current += 1;

    if (!trackEvent) return;

    trackEvent('bid_submit_attempt_failed', {
      reason: 'validation_error',
      errorType: determineErrorType(validation),
      attemptedAmount: amount,
      minimumRequired: property.minAbsolute,
      currentHighest: currentPrice,
      selectedConditions: selectedConditions,
      hasCustomText: conditionsText.trim().length > 0,
      attemptNumber: submitAttemptsRef.current,
      timeInForm: getTimeInForm(),
      amountChanges: amountChangesRef.current,
      conditionChanges: conditionChangesRef.current
    });
  };

  /**
   * Tracking: Envío exitoso (pasa validación)
   */
  const trackSubmitSuccess = (amount) => {
    if (!trackEvent) return;

    trackEvent('bid_submit_success', {
      amount,
      selectedConditions,
      hasCustomText: conditionsText.trim().length > 0,
      customTextLength: conditionsText.length,
      timeInForm: getTimeInForm(),
      amountChanges: amountChangesRef.current,
      conditionChanges: conditionChangesRef.current,
      attemptNumber: submitAttemptsRef.current + 1
    });
  };

  /**
   * Tracking: Modal de confirmación abierto
   */
  const trackConfirmModalOpened = (amount, requiresPinCreation) => {
    if (!trackEvent) return;

    trackEvent('bid_confirm_modal_opened', {
      amount,
      requiresPinCreation
    });
  };

  /**
   * Tracking: Modal de confirmación abandonado
   */
  const trackConfirmModalAbandoned = (amount) => {
    if (!trackEvent) return;

    trackEvent('bid_confirm_modal_abandoned', {
      amount,
      reason: 'closed_modal'
    });
    
    confirmAttemptsRef.current = 0;
  };

  /**
   * Tracking: Error en confirmación (montos no coinciden)
   */
  const trackConfirmAmountMismatch = (originalAmount, confirmedAmount) => {
    confirmAttemptsRef.current += 1;

    if (!trackEvent) return;

    trackEvent('bid_confirm_amount_mismatch', {
      originalAmount,
      confirmedAmount,
      attemptNumber: confirmAttemptsRef.current
    });
  };

  /**
   * Tracking: EMAIL DE VERIFICACIÓN ENVIADO 📧
   */
  const trackBidVerificationEmailSent = (amount, isFirstBid) => {
    if (!trackEvent) return;

    trackEvent('bid_verification_email_sent', {
      amount,
      totalTimeFromFormFocus: getTimeInForm(),
      amountChanges: amountChangesRef.current,
      conditionChanges: conditionChangesRef.current,
      validationErrors: submitAttemptsRef.current,
      confirmAttempts: confirmAttemptsRef.current,
      selectedConditions,
      hasCustomText: conditionsText.trim().length > 0,
      customTextLength: conditionsText.length,
      isFirstBid
    });
  };

  /**
   * Tracking: Error al crear oferta
   */
  const trackBidCreationError = (amount, error) => {
    if (!trackEvent) return;

    trackEvent('bid_creation_error', {
      amount,
      error: error.message
    });
  };

  // ============================================
  // RETURN: API pública del hook
  // ============================================

  return {
    // Inicialización
    initFormTimer,
    resetTracking,
    
    // Tracking de cambios
    trackConditionsChange,
    trackCustomConditionsWritten,
    
    // Tracking de envío
    trackSubmitFailed,
    trackSubmitSuccess,

    // Tracking de confirmación
    trackConfirmModalOpened,
    trackConfirmModalAbandoned,
    trackConfirmAmountMismatch,
    
    // Tracking de completado
    trackBidVerificationEmailSent,
    trackBidCreationError,
    
    // Acceso a contadores (para debug)
    getMetrics: () => ({
      timeInForm: getTimeInForm(),
      amountChanges: amountChangesRef.current,
      conditionChanges: conditionChangesRef.current,
      submitAttempts: submitAttemptsRef.current,
      confirmAttempts: confirmAttemptsRef.current
    })
  };
};

export default useBiddingTracking;